import React from 'react';
import styled from 'styled-components';
import '@reach/tooltip/styles.css';
import Theme from './theme';
import { Tooltip } from './tooltips';
import type { DataProvidesProps } from '../../utils/constants';
import { getDataProvidesProps } from '../../utils/constants';

type HelpTooltipProps = {
  /**
   * Represents the text that is displayed within the tooltip content.
   */
  label: string,
  /**
   * The accessibility label for assistive technology users.
   */
  ariaLabel: string,
  ...DataProvidesProps,
};

const TooltipQuestionIcon = () => {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        data-tooltip-question-icon-background
        d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z"
        fill="#E0E5E3"
      />
      <path
        data-tooltip-question-icon-border
        d="M9 0.0200195C4.05 0.0200195 0.0199966 4.05002 0.0199966 9.00002C0.0199966 13.95 4.05 17.98 9 17.98C13.95 17.98 17.98 13.95 17.98 9.00002C17.98 4.05002 13.95 0.0200195 9 0.0200195ZM9 16.81C4.69 16.81 1.19 13.31 1.19 9.00002C1.19 4.69002 4.69 1.19002 9 1.19002C13.31 1.19002 16.81 4.69002 16.81 9.00002C16.81 13.31 13.31 16.81 9 16.81Z"
        fill="#E0E5E3"
      />
      <path
        data-tooltip-question-icon
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.01411 5.9491C6.36411 4.4691 7.74411 3.3891 9.35412 3.5091C10.9141 3.6291 12.1741 4.9091 12.2641 6.4691C12.3541 8.0491 11.2841 9.3991 9.82412 9.7391C9.74412 9.7591 9.68412 9.8391 9.68412 9.9191V11.2491C9.68412 11.5691 9.42412 11.8291 9.10412 11.8291H9.09412C8.77412 11.8291 8.51411 11.5691 8.51411 11.2491V9.1991C8.51411 8.8991 8.75411 8.6591 9.05412 8.6591C10.1341 8.6591 11.0741 7.7891 11.0941 6.7091C11.1141 5.5791 10.2141 4.6591 9.09412 4.6591C8.14411 4.6591 7.34411 5.3191 7.14411 6.2091C7.08411 6.4791 6.84411 6.6591 6.57411 6.6591C6.20411 6.6591 5.93411 6.3091 6.01411 5.9491ZM9.87412 13.6491C9.87412 14.0744 9.52938 14.4191 9.10412 14.4191C8.67886 14.4191 8.33412 14.0744 8.33412 13.6491C8.33412 13.2239 8.67886 12.8791 9.10412 12.8791C9.52938 12.8791 9.87412 13.2239 9.87412 13.6491Z"
        fill="#15372C"
      />
    </svg>
  );
};

const QuestionIconWrapper = styled.span.attrs(() => ({
  tabIndex: 0,
}))`
  display: inline-flex;

  [data-tooltip-question-icon-background] {
    fill: ${Theme.helpIcon.default.backgroundColor};
  }

  &:hover {
    [data-tooltip-question-icon-background] {
      fill: ${Theme.helpIcon.hover.backgroundColor};
    }

    [data-tooltip-question-icon-border] {
      fill: ${Theme.helpIcon.hover.borderColor};
    }

    [data-tooltip-question-icon] {
      fill: ${Theme.helpIcon.hover.color};
    }
  }

  &:focus {
    [data-tooltip-question-icon-background] {
      fill: ${Theme.helpIcon.focus.backgroundColor};
    }

    [data-tooltip-question-icon-border] {
      fill: ${Theme.helpIcon.focus.borderColor};
    }

    [data-tooltip-question-icon] {
      fill: ${Theme.helpIcon.focus.color};
    }
  }

  &:focus-visible,
  svg:focus,
  svg:focus-visible {
    outline: none;
  }

  &:active {
    [data-tooltip-question-icon-background] {
      fill: ${Theme.helpIcon.active.backgroundColor};
    }

    [data-tooltip-question-icon-border] {
      fill: ${Theme.helpIcon.active.borderColor};
    }

    [data-tooltip-question-icon] {
      fill: ${Theme.helpIcon.active.color};
    }
  }
`;

const HelpTooltip = (props: HelpTooltipProps) => {
  const { label, ariaLabel } = props;

  return (
    <Tooltip
      label={label}
      ariaLabel={ariaLabel}
      {...getDataProvidesProps(props)}
    >
      <QuestionIconWrapper>
        <TooltipQuestionIcon />
      </QuestionIconWrapper>
    </Tooltip>
  );
};

HelpTooltip.defaultProps = {
  'data-provides': 'help-tooltip',
};

export default HelpTooltip;
